import {
  createLabelFormatting,
  deleteLabelFormatting,
  reorderLabelFormatting,
  updateLabelFormattingAt,
  perspectivesEditor$,
  showReferenceTypeToggled,
  labelFormattingAiButtonClicked,
} from './perspectivesEditor$';
import { dispatchAction, spreadMap } from '@ardoq/rxbeach';
import { combineLatest } from 'rxjs';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { LabelsTab, getAddLabelButtonProps } from '@ardoq/perspectives-sidebar';
import { activeView$ } from 'streams/views/mainContent/activeView$';
import { APIFieldAttributes, APIFieldType, ViewIds } from '@ardoq/api-types';
import { ComponentProps } from 'react';
import type { LabelFormattingInfo } from '@ardoq/data-model';
import { getComponentTypes, getReferenceTypes } from '@ardoq/graph';
import { PermissionContext } from '@ardoq/access-control';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { fields$, FieldsState } from 'streams/fields/fields$';
import { globalFields$, GlobalFieldsState } from 'streams/fields/globalFields$';
import { dateRangeOperations } from '@ardoq/date-range';
import {
  getFieldOptionsByComponentTypes,
  getFieldOptionsByReferenceTypes,
  getFieldOptionLabel,
  getLabelFormattingElementProps,
  getDisabledReferenceFormattingMessage,
  getRestrictedMultiLabelFormattingWarningProps,
} from './multiLabelUtils';
import { isPerspectivesAiButtonVisible$ } from './isPerspectivesAiButtonVisible$';

const toFieldTypesByName = (
  byName: ReadonlyMap<string, APIFieldAttributes>
): Map<string, APIFieldType> => {
  const result = new Map<string, APIFieldType>();
  for (const [name, field] of byName) {
    result.set(name, field.type);
  }
  return result;
};

const toFieldLabelByName = (
  fieldsState: FieldsState,
  globalFieldsState: GlobalFieldsState
): Map<string, string> => {
  const result = new Map<string, string>();
  for (const field of globalFieldsState.models) {
    result.set(field.name, getFieldOptionLabel(field));
  }
  for (const [name, field] of fieldsState.byName) {
    if (
      !result.has(name) &&
      dateRangeOperations.fieldNameIsPartOfDateRangeField(name)
    ) {
      result.set(name, getFieldOptionLabel(field));
    }
  }
  return result;
};

type GetMultiLabelFormattingPropsArgs = {
  showReferenceType: boolean;
  labelFormatting: LabelFormattingInfo[];
  mainViewId: ViewIds;
  componentIds?: Set<string>;
  referenceIds?: Set<string>;
  permissionContext: PermissionContext;
  fieldTypesByName: ReadonlyMap<string, APIFieldType>;
  fieldsByComponentType: ReadonlyMap<string, APIFieldAttributes[]>;
  fieldsByReferenceType: ReadonlyMap<string, APIFieldAttributes[]>;
  fieldLabelByName: ReadonlyMap<string, string>;
  i18nLocale: I18nLocale;
};

const getMultiLabelFormattingProps = ({
  labelFormatting,
  showReferenceType,
  mainViewId,
  componentIds,
  referenceIds,
  permissionContext,
  fieldTypesByName,
  fieldsByComponentType,
  fieldsByReferenceType,
  fieldLabelByName,
  i18nLocale,
}: GetMultiLabelFormattingPropsArgs) => {
  const componentTypes = getComponentTypes([...(componentIds ?? [])]);
  const referenceTypes = getReferenceTypes([...(referenceIds ?? [])]);

  const componentFieldOptionsByTypes = getFieldOptionsByComponentTypes(
    componentTypes,
    fieldsByComponentType
  );
  const referenceFieldOptionsByTypes = getFieldOptionsByReferenceTypes(
    referenceTypes,
    fieldsByReferenceType
  );

  const disableReferenceFormattingMessage =
    getDisabledReferenceFormattingMessage(mainViewId, i18nLocale);

  const labelFormattingElementProps = getLabelFormattingElementProps(
    labelFormatting,
    componentFieldOptionsByTypes,
    referenceFieldOptionsByTypes,
    Boolean(disableReferenceFormattingMessage),
    permissionContext,
    fieldTypesByName,
    fieldLabelByName,
    i18nLocale
  );

  const addLabelButtonProps = getAddLabelButtonProps({
    onLabelFormattingAdded: onLabelFormattingAdded,
    hasNoReferences: !referenceTypes.length,
    disableReferenceFormattingMessage,
  });

  const restrictedMultiLabelFormattingWarningProps =
    getRestrictedMultiLabelFormattingWarningProps({
      labelFormatting,
      viewId: mainViewId,
    });

  return {
    labelFormatting,
    showReferenceType,
    labelFormattingElementProps,
    disableReferenceFormattingMessage,
    addLabelButtonProps,
    restrictedMultiLabelFormattingWarningProps,
    onLabelFormattingAdded,
    onLabelFormattingReordered,
    onLabelFormattingDeleted,
    onUpdateLabelFormattingAt,
    onShowReferenceTypeToggled,
  };
};

const labelsTab$ = combineLatest([
  perspectivesEditor$,
  activeView$,
  currentUserPermissionContext$,
  fields$,
  globalFields$,
  i18nLocale$,
  isPerspectivesAiButtonVisible$,
]).pipe(
  spreadMap(
    (
      state,
      { mainViewId },
      permissionContext,
      fieldsState,
      globalFieldsState,
      i18nLocale,
      isAiButtonVisible
    ): ComponentProps<typeof LabelsTab> & WithI18nLocale => {
      const { formattingTab, datasetRelatedData } = state;
      const { labelFormatting, showReferenceType } = formattingTab;
      const fieldTypesByName = toFieldTypesByName(fieldsState.byName);
      const fieldLabelByName = toFieldLabelByName(
        fieldsState,
        globalFieldsState
      );

      const onAiButtonClicked = () =>
        dispatchAction(labelFormattingAiButtonClicked());
      return {
        ...getMultiLabelFormattingProps({
          labelFormatting,
          showReferenceType,
          mainViewId,
          componentIds: datasetRelatedData?.componentIds,
          referenceIds: datasetRelatedData?.referenceIds,
          permissionContext,
          fieldTypesByName,
          fieldsByComponentType: fieldsState.byComponentType,
          fieldsByReferenceType: fieldsState.byReferenceType,
          fieldLabelByName,
          i18nLocale,
        }),
        isAiButtonVisible,
        onAiButtonClicked,
        i18nLocale,
      };
    }
  )
);

const onLabelFormattingAdded = (labelFormatting: LabelFormattingInfo) =>
  dispatchAction(createLabelFormatting(labelFormatting));

const onLabelFormattingReordered = (
  reorderedLabelFormatting: LabelFormattingInfo[]
) => dispatchAction(reorderLabelFormatting(reorderedLabelFormatting));

const onLabelFormattingDeleted = (deletedLabelFormattingIndex: number) =>
  dispatchAction(deleteLabelFormatting(deletedLabelFormattingIndex));

const onUpdateLabelFormattingAt = (
  index: number,
  value: Partial<LabelFormattingInfo>
) => dispatchAction(updateLabelFormattingAt({ index, ...value }));

const onShowReferenceTypeToggled = (showReferenceType: boolean) =>
  dispatchAction(showReferenceTypeToggled(showReferenceType));

export { labelsTab$ };
